import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import memoize from "memoize-one";
import {Icon, Input, Select, ButtonGroup, Button} from "spectre-react/dist/cjs";
import {NEW_CLIENT_PATH, NEW_REPORT_PATH, editMachinePath, editClientPath} from "../paths";
import {connect} from "react-redux";
import {getClientsWithMachines} from "../selectors";
import {ClientContactPropType} from "./shared/propTypes";
import {CLIENT_GEO_ZONE, CLIENT_GEO_ZONE_LABEL} from "./shared/constants";
import {debounce} from "./shared/utils";


const COLUMN = {
    NAME: 'NAME',
    MACHINES: 'MACHINES',
    PERSONS: 'PERSONS',
    ADDRESS: 'ADDRESS',
    BANK_DETAILS: 'BANK_DETAILS',
    CONTRACTS: 'CONTRACTS',
    NOTES: 'NOTES'
}

const getZoneColorClass = (geoZone) => {
    switch (geoZone) {
        case CLIENT_GEO_ZONE.HOME: return 'geoZone__home';
        case CLIENT_GEO_ZONE.OTHER: return 'geoZone__other';
    }
}

const filterClients = memoize(
    (clients, filters) => {
        for (const key in filters) {
            const re = new RegExp(filters[key], 'i');

            switch (key) {
                case COLUMN.NAME: clients = clients.filter(c => re.test(c.name) || re.test(c.jurName) || re.test(c.website)); break;
                case COLUMN.MACHINES: clients = clients.filter(c => c.machines.some(m => re.test(m.name) || re.test(m.serial))); break;
                case COLUMN.PERSONS: clients = clients.filter(c => c.contacts.some(_c => re.test(_c.name) || re.test(_c.phone) || re.test(_c.email))); break;
                case COLUMN.ADDRESS: clients = clients.filter(c => re.test(c.address)); break;
                case COLUMN.BANK_DETAILS: clients = clients.filter(c => re.test(c.bankDetails)); break;
                case COLUMN.NOTES: clients = clients.filter(c => re.test(c.notes)); break;
                case COLUMN.CONTRACTS: clients = clients.filter(c => re.test(c.contracts)); break;
                default: break;
            }
        }

        return clients;
    }
);

// ------------

const MachinesColumn = ({clientId, machines, currentUser}) => {
    const {isEngineer, isManagement} = currentUser;

    return (
        <table>
            <tbody>
            {machines.map(({_id, manufacturer, model, serial}) => {
                return (
                    <tr key={_id}>
                        <td>
                            <Link to={editMachinePath(_id)} className="tooltip" data-tooltip="Редактировать">{manufacturer} {model}</Link>
                        </td>
                        <td className="m-serial">{
                            (isEngineer || isManagement)
                                ? <Link to={{pathname: NEW_REPORT_PATH, state: {c: clientId, m: _id}}} className="tooltip" data-tooltip="Новый репорт">{serial}</Link>
                                : serial
                        }</td>
                    </tr>
                );
            })}
            </tbody>
        </table>
    )
}


class ContactsColumn extends React.PureComponent {
    static propTypes = {
        contacts: PropTypes.arrayOf(ClientContactPropType)
    };

    state = { page: 0 };

    setPage = (ev) => this.setState({ page: parseInt(ev.target.dataset.index, 10) })

    renderContact = ({name, phone, email}) => {
        return (
            <div>
                <Icon icon="people" className="ml-2 mr-1" title="ФИО"/> {name}<br/>
                <Icon icon="message" className="ml-2 mr-1" title="Телефон"/> {phone}<br/>
                <Icon icon="mail" className="ml-2 mr-1" title="Email"/> {email}<br/>
            </div>
        )
    }

    render() {
        const page = this.state.page;
        let contacts = this.props.contacts;

        if (contacts.length === 0)
            return null;

        if (contacts.length === 1)
            return this.renderContact(contacts[0]);

        return (
            <div>
                <ButtonGroup block className="mb-2">
                    {contacts.map((contact, index) =>
                        <Button
                            key={contact.name + contact.phone + contact.email}
                            small
                            link={page !== index}
                            onClick={this.setPage}
                            data-index={index}
                            className="text-ellipsis"
                        >
                            {contact.name || "?"}
                        </Button>
                    )}
                </ButtonGroup>
                {this.renderContact(contacts[page])}
            </div>
        )
    }
}


const AddressColumn = ({client}) => {
    const {geoZone, address, mapUrl} = client;

    const geoZoneCls = getZoneColorClass(geoZone);
    const geoZoneLabel = CLIENT_GEO_ZONE_LABEL[geoZone];

    return (
        <div className="column__address">
            <div className="mb-2">
                Зона: <span className={geoZoneCls}>{geoZoneLabel}</span>
            </div>

            {address && (
                <div className="mb-2">
                    Адрес: <span>{address}</span>
                </div>
            )}

            {mapUrl && (
                <a className="mr-1" href={mapUrl} target="_blank" rel="noopener noreferrer">
                    <span className="material-icons-outlined">map</span> <span>Показать на картах</span>
                </a>
            )}
        </div>
    );
}


const ContractsColumn = ({client}) => {
    const {aristoService, durstService, durstRepair} = client;
    const contracts = [];

    if (aristoService.active) {
        const {comment, refund} = aristoService;
        contracts.push(`• Сервисный рамочный Aristo: ${comment} ${refund ? ' (с возмещением расходов)' : ''}`);
    }
    if (durstService.active) {
        const {comment, refund} = durstService;
        contracts.push(`• Сервисный рамочный Durst: ${comment} ${refund ? ' (с возмещением расходов)' : ''}`);
    }
    if (durstRepair.active)
        contracts.push(`• Восстановление голов Durst: ${durstRepair.comment}`);

    return (
        <div>{contracts.join('\n')}</div>
    )
}


// ------------


class ClientsList extends React.Component {
    static propTypes = {
        currentUser: PropTypes.object.isRequired,
        clients: PropTypes.arrayOf(PropTypes.shape({
            _id: PropTypes.string,
            _rev: PropTypes.string,
            name: PropTypes.string.isRequired,
            jurName: PropTypes.string,
            website: PropTypes.string,
            address: PropTypes.string,
            mapUrl: PropTypes.string,
            notes: PropTypes.string,
            bankDetails: PropTypes.string,
            contacts: PropTypes.arrayOf(ClientContactPropType),
            contracts: PropTypes.string,
            machines: PropTypes.arrayOf(PropTypes.shape({
                _id: PropTypes.string,
                name: PropTypes.string,
                serial: PropTypes.string
            })),
        }))
    };

    N = 25;

    state = {
        shownNum: this.N,
        freeColumn: COLUMN.ADDRESS,
        filters: {},
        clients: this.props.clients,
    };

    componentDidMount() {
        // [document.body.scrollHeight - window.innerHeight, window.scrollY]
        window.addEventListener('scroll', this.onScroll);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.onScroll);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.clients !== this.props.clients) {
            this.updateClients();
        }
    }

    onScroll = () => {
        if (document.body.scrollHeight - window.innerHeight - 400 < window.scrollY &&
            this.state.shownNum < this.state.clients.length)
            this.setState({shownNum: this.state.shownNum + this.N});
    };

    updateClients = debounce(
        () => {
            const clients = filterClients(this.props.clients, this.state.filters);
            this.setState({ clients });
        },
        300);

    onFilterChange = (ev) => {
        const {type, name, value, checked} = ev.target;
        const filters = Object.assign({}, this.state.filters);

        if (type === "checkbox" && checked) filters[name] = true;
        else if (type !== "checkbox" && value) filters[name] = value;
        else delete filters[name];

        this.setState({filters, shownNum: this.N}, this.updateClients);
    };

    onColumnSelect = (ev) => {
        if (ev.target.value !== this.state.freeColumn) {
            const filters = Object.assign({}, this.state.filters);
            delete filters[this.state.freeColumn];
            this.setState({freeColumn: ev.target.value, filters, shownNum: this.N});
        }
    };

    render() {
        const currentUser = this.props.currentUser;
        const {freeColumn, filters, shownNum} = this.state;
        const clients = this.state.clients.slice(0, shownNum);

        return (
            <div>
                <Link className="btn btn-primary" to={NEW_CLIENT_PATH}>Создать клиента</Link>

                <div id="clients-table" className="custom-table">
                    <div className="table-row table-head" key={0}>
                        <div className="table-head-cell">
                            <div className="mb-2">Клиент</div>
                            <Input small name={COLUMN.NAME} value={filters.name} onChange={this.onFilterChange} />
                        </div>
                        <div className="table-head-cell">
                            <div className="mb-2">Оборудование</div>
                            <Input small name={COLUMN.MACHINES} value={filters.machines} onChange={this.onFilterChange} />
                        </div>
                        <div className="table-head-cell">
                            <div className="mb-2">Контактные лица</div>
                            <Input small name={COLUMN.PERSONS} value={filters.contacts} onChange={this.onFilterChange}/>
                        </div>
                        <div className="table-head-cell">
                            <Select small className="mb-1" value={freeColumn} onChange={this.onColumnSelect}>
                                <option value={COLUMN.ADDRESS}>Адрес</option>
                                <option value={COLUMN.BANK_DETAILS}>Реквизиты</option>
                                <option value={COLUMN.CONTRACTS}>Контракты</option>
                                <option value={COLUMN.NOTES}>Заметки</option>
                            </Select>
                            <input name={freeColumn} className="form-input input-sm" value={filters[freeColumn]} onChange={this.onFilterChange}/>
                        </div>
                    </div>

                    {clients.map(client =>
                        <div className="table-row" key={client._id}>
                            <div>
                                <div>
                                    <Link to={editClientPath(client)} className="tooltip" data-tooltip="Редактировать">{client.name}</Link>
                                </div>
                                {(client.jurName || client.website) && <hr/>}
                                {client.jurName && <div>Юр. лицо: {client.jurName}</div>}
                                {client.website && <div>Веб-сайт: <a href={client.website} target="_blank noreferrer noopener">{client.website}</a></div>}
                            </div>
                            <div className="column__machines">
                                <MachinesColumn clientId={client._id} machines={client.machines} currentUser={currentUser}/>
                            </div>
                            <div className="column__contacts">
                                <ContactsColumn contacts={client.contacts} />
                            </div>
                            {freeColumn === COLUMN.ADDRESS && <AddressColumn client={client} />}
                            {freeColumn === COLUMN.BANK_DETAILS && <div className="ws-pl">{client.bankDetails}</div>}
                            {freeColumn === COLUMN.NOTES && <div className="ws-pl">{client.notes}</div>}
                            {freeColumn === COLUMN.CONTRACTS && <ContractsColumn client={client} />}
                        </div>
                    )}
                </div>
            </div>
        )
    }
}


function mapStateToProps(state, ownProps) {
    return { clients: getClientsWithMachines(state) };
}

export default connect(mapStateToProps)(ClientsList);